// Making a thing inside a schedule, and changing one.
//
// Both sheets fetch before they draw, for reasons that are not the same:
//
//   NEW asks for `GET /sections`, because the schedule it goes into may be one
//   with nothing due today - and the day payload leaves those out, so the cards
//   on screen are not the account's schedules and must never be listed as if
//   they were (AGENTS.md).
//
//   EDIT asks for `GET /routines/:id`, because the day payload carries the
//   recurrence in WORDS and not the rule itself. An editor that offered to
//   change the repeat while holding only "Mon, Wed, Fri" would be guessing at
//   the rule it is about to overwrite.

import { useState } from 'react';
import { api } from '../api/client.ts';
import type { Routine, RoutinePatch, Section } from '../api/client.ts';
import { Icon } from '../day/Icon.tsx';
import { dayAndMonth } from '../iso-day.ts';
import { EveningToggle } from './ScheduleSheet.tsx';
import { RepeatField } from './RepeatField.tsx';
import { Chips, Field, FieldGroup, Notice, Sheet, Stuck, useOnce, Waiting } from './Sheet.tsx';
import {
  choosableSections,
  isEditableItem,
  repeatIsComplete,
  repeatOf,
  toggleDay,
  toRule,
} from './edit-model.ts';
import type { Repeat } from './edit-model.ts';
import type { NewItemInput } from './useEditor.ts';

function Problem({ problem }: { problem: string | null }) {
  return problem === null ? null : (
    <p className="sheetproblem" role="alert">
      {problem}
    </p>
  );
}

// ------------------------------------------------------------------- new ----

interface NewProps {
  /** The schedule to start in, when the sheet was opened from one. */
  sectionId: string | null;
  onceOn: string;
  busy: boolean;
  problem: string | null;
  onCreate: (input: NewItemInput) => void;
  onNewSchedule: () => void;
  onSignedOut: () => void;
  onClose: () => void;
}

export function NewItemSheet({
  sectionId,
  onceOn,
  busy,
  problem,
  onCreate,
  onNewSchedule,
  onSignedOut,
  onClose,
}: NewProps) {
  // One subject: the account's schedules, which is not keyed by anything.
  const sections = useOnce('sections', () => api.sections(), onSignedOut);

  if (sections.status === 'loading')
    return <Waiting title="Add something to do" onClose={onClose} />;
  if (sections.status === 'failed') {
    return <Stuck title="Add something to do" message={sections.message} onClose={onClose} />;
  }

  const choices = choosableSections(sections.value);
  if (choices.length === 0) {
    return (
      <Sheet
        title="Add something to do"
        onClose={onClose}
        footer={
          <>
            <button className="btn" onClick={onClose}>
              Cancel
            </button>
            <button className="btn primary" onClick={onNewSchedule}>
              Make a schedule
            </button>
          </>
        }
      >
        <h3>There is nowhere to put it yet.</h3>
        <p className="sub">
          Everything you do lives in a schedule - Work, School, Gym, or anything you name. Make one
          and this thing can go in it.
        </p>
      </Sheet>
    );
  }

  return (
    <NewItemForm
      choices={choices}
      // The schedule it was opened from, when that schedule is still one that
      // can be written to; otherwise the first, so the field is never empty.
      initialSectionId={choices.find((s) => s.id === sectionId)?.id ?? choices[0]?.id ?? ''}
      onceOn={onceOn}
      busy={busy}
      problem={problem}
      onCreate={onCreate}
      onClose={onClose}
    />
  );
}

function NewItemForm({
  choices,
  initialSectionId,
  onceOn,
  busy,
  problem,
  onCreate,
  onClose,
}: {
  choices: Section[];
  initialSectionId: string;
  onceOn: string;
  busy: boolean;
  problem: string | null;
  onCreate: (input: NewItemInput) => void;
  onClose: () => void;
}) {
  const [title, setTitle] = useState('');
  const [chosen, setChosen] = useState(initialSectionId);
  const [repeat, setRepeat] = useState<Repeat>('every');
  const [days, setDays] = useState<number[]>([]);
  const [evening, setEvening] = useState(false);
  const [note, setNote] = useState('');

  const ready = title.trim() !== '' && chosen !== '' && repeatIsComplete(repeat, days);

  return (
    <Sheet
      title="Add something to do"
      onClose={onClose}
      footer={
        <>
          <button className="btn" disabled={busy} onClick={onClose}>
            Cancel
          </button>
          <button
            className="btn primary"
            disabled={!ready || busy}
            onClick={() =>
              onCreate({
                sectionId: chosen,
                title: title.trim(),
                note,
                rule: toRule(repeat, days),
                evening,
              })
            }
          >
            {busy ? 'Adding…' : 'Add it'}
          </button>
        </>
      }
    >
      <h3>Add something to do</h3>
      <p className="sub">One thing, inside one of your schedules.</p>

      <Field label="What is it?">
        {(id) => (
          <input
            id={id}
            type="text"
            autoFocus
            autoComplete="off"
            placeholder="Water the plants"
            value={title}
            onChange={(event) => setTitle(event.target.value)}
          />
        )}
      </Field>

      {/* Every schedule on the account, not the cards on screen: a schedule
          with nothing due today is missing from the day and would be missing
          from here too if this counted them. */}
      <FieldGroup label="Which schedule?">
        <Chips
          options={choices.map((section) => ({ key: section.id, label: section.name }))}
          chosen={[chosen]}
          onChoose={setChosen}
        />
      </FieldGroup>

      <RepeatField
        repeat={repeat}
        days={days}
        onRepeat={setRepeat}
        onToggleDay={(day) => setDays((chosen) => toggleDay(chosen, day))}
        onceOn={onceOn}
      />

      <EveningToggle evening={evening} onChange={setEvening} />

      <Field label="Note">
        {(id) => (
          <textarea
            id={id}
            rows={3}
            placeholder="Anything you want to remember about this one."
            value={note}
            onChange={(event) => setNote(event.target.value)}
          />
        )}
      </Field>

      <Problem problem={problem} />
    </Sheet>
  );
}

// ------------------------------------------------------------------ edit ----

interface EditProps {
  itemId: string;
  /**
   * The recurrence in the SERVER'S words, taken from the day row rather than
   * phrased here. `describeRecurrence` runs on the server precisely so that two
   * clients cannot describe one rule two ways (AGENTS.md), and the only place
   * this sheet needs the words is where its own four chips have nothing to say.
   */
  meta: string;
  busy: boolean;
  conflicted: boolean;
  problem: string | null;
  onSave: (id: string, patch: RoutinePatch) => void;
  onArchive: (id: string, title: string) => void;
  onDeleteForever: (id: string, title: string) => void;
  onSignedOut: () => void;
  onClose: () => void;
}

export function EditItemSheet({
  itemId,
  meta,
  busy,
  conflicted,
  problem,
  onSave,
  onArchive,
  onDeleteForever,
  onSignedOut,
  onClose,
}: EditProps) {
  const routine = useOnce(itemId, (id) => api.routine(id), onSignedOut);

  if (routine.status === 'loading') return <Waiting title="Edit item" onClose={onClose} />;
  if (routine.status === 'failed') {
    return <Stuck title="Edit item" message={routine.message} onClose={onClose} />;
  }

  // Nothing in this client opens a sheet on a step - the day screen ticks them
  // and offers no edit - so this is the second lock on a door already shut. A
  // step is refused by the API for every write (`steps-arrive-in-phase-2`), and
  // a form that could not save is worse than a sentence saying so.
  if (!isEditableItem(routine.value)) {
    return (
      <Sheet
        title={routine.value.title}
        onClose={onClose}
        footer={
          <button className="btn primary" onClick={onClose}>
            Close
          </button>
        }
      >
        <h3>{routine.value.title}</h3>
        <Notice>
          This one is not editable here yet. Steps can be read and ticked off, and changing them
          arrives with the screen that draws them.
        </Notice>
      </Sheet>
    );
  }

  return (
    <EditItemForm
      routine={routine.value}
      meta={meta}
      busy={busy}
      conflicted={conflicted}
      problem={problem}
      onSave={onSave}
      onArchive={onArchive}
      onDeleteForever={onDeleteForever}
      onClose={onClose}
    />
  );
}

function EditItemForm({
  routine,
  meta,
  busy,
  conflicted,
  problem,
  onSave,
  onArchive,
  onDeleteForever,
  onClose,
}: {
  routine: Routine;
  meta: string;
  busy: boolean;
  conflicted: boolean;
  problem: string | null;
  onSave: (id: string, patch: RoutinePatch) => void;
  onArchive: (id: string, title: string) => void;
  onDeleteForever: (id: string, title: string) => void;
  onClose: () => void;
}) {
  const stored = repeatOf(routine.rule);
  const [title, setTitle] = useState(routine.title);
  const [note, setNote] = useState(routine.note);
  const [evening, setEvening] = useState(routine.evening);
  // Null while the stored rule is one the four chips cannot say. Staying null
  // is what leaves that rule alone: `rule` is left out of the patch entirely,
  // so a monthly repeat survives an edit to the title.
  const [repeat, setRepeat] = useState<Repeat | null>(stored?.repeat ?? null);
  const [days, setDays] = useState<number[]>(stored?.days ?? []);
  const [confirming, setConfirming] = useState(false);

  const trimmed = title.trim();
  const ready = trimmed !== '' && (repeat === null || repeatIsComplete(repeat, days));

  const patch = (): RoutinePatch => {
    const storedNote = routine.note.trim() === '' ? '' : routine.note;
    const nextNote = note.trim() === '' ? '' : note;
    return {
      title: trimmed,
      ...(nextNote === storedNote
        ? {}
        : {
            note: nextNote,
            ...(nextNote === ''
              ? {
                  noteClearExpectedNote: storedNote,
                  noteClearExpectedRevision: routine.revision,
                }
              : {}),
          }),
      evening,
      ...(repeat === null ? {} : { rule: toRule(repeat, days) }),
    };
  };

  return (
    <Sheet
      title={routine.title}
      onClose={onClose}
      footer={
        <>
          <button className="btn" disabled={busy} onClick={onClose}>
            Cancel
          </button>
          <button
            className="btn primary"
            disabled={!ready || busy || conflicted}
            onClick={() => onSave(routine.id, patch())}
          >
            {busy ? 'Saving…' : 'Save'}
          </button>
        </>
      }
    >
      <h3>{routine.title}</h3>

      <Field label="What is it?">
        {(id) => (
          <input
            id={id}
            type="text"
            autoComplete="off"
            value={title}
            onChange={(event) => setTitle(event.target.value)}
          />
        )}
      </Field>

      <RepeatField
        repeat={repeat}
        days={days}
        onRepeat={setRepeat}
        onToggleDay={(day) => setDays((chosen) => toggleDay(chosen, day))}
        currentWords={meta}
        // The item's OWN first day, not the day on screen: switching an
        // existing thing to "just once" leaves it due on the day it started,
        // which is a different day from the one being looked at and is the
        // whole reason the sentence names it.
        onceOn={dayAndMonth(routine.anchorDay)}
      />

      <EveningToggle evening={evening} onChange={setEvening} />

      <Field label="Note">
        {(id) => (
          <textarea
            id={id}
            rows={4}
            placeholder="Anything you want to remember about this one."
            value={note}
            onChange={(event) => setNote(event.target.value)}
          />
        )}
      </Field>

      <Notice>
        Archiving takes it off your day and keeps it, so you can put it back. Deleting removes the
        item for good. Either way, the days you have already finished still count in Progress.
      </Notice>

      <div className="rowbtns">
        <button
          className="btn"
          disabled={busy}
          onClick={() => onArchive(routine.id, routine.title)}
        >
          <Icon name="archive" />
          Archive
        </button>
        {confirming ? (
          <button
            className="btn danger"
            disabled={busy}
            onClick={() => onDeleteForever(routine.id, routine.title)}
          >
            <Icon name="trash" />
            Yes, delete forever
          </button>
        ) : (
          <button className="btn danger" disabled={busy} onClick={() => setConfirming(true)}>
            <Icon name="trash" />
            Delete forever
          </button>
        )}
      </div>

      {confirming && (
        <p className="sheetproblem" role="alert">
          &quot;{routine.title}&quot; will be gone, and this one cannot be undone.
        </p>
      )}

      <Problem problem={problem} />
    </Sheet>
  );
}
